import { useEffect, useState } from 'react';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import type { CarlineOption } from '../stis-inline-filters';
import { toTrimOptions, type TrimOption } from '../vehicle-filter.utils';

const logger = getLogger('vin-sessions');

type Params = {
    isSignedIn: boolean;
    modelYear?: string;
    carlineId?: string;
    fallbackModelYears: (string | number)[];
    fallbackCarlines: CarlineOption[];
};

export type VehicleFilterOptions = {
    modelYears: string[];
    carlines: CarlineOption[];
    trims: TrimOption[];
    supportsTrim: boolean;
};

export const useVehicleFilterOptions = ({
    isSignedIn,
    modelYear,
    carlineId,
    fallbackModelYears,
    fallbackCarlines,
}: Params): VehicleFilterOptions => {
    const [modelYears, setModelYears] = useState<string[]>([]);
    const [carlines, setCarlines] = useState<CarlineOption[]>([]);
    const [trims, setTrims] = useState<TrimOption[]>([]);

    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        const controller = new AbortController();
        docsService
            .getVehicleModelYears({ signal: controller.signal })
            .then((response) => {
                if (!controller.signal.aborted && response.success) {
                    setModelYears(response.data.map(String));
                }
            })
            .catch((error) => logger.error('Failed to fetch the vehicle model years', { error }));
        return () => controller.abort();
    }, [isSignedIn]);

    useEffect(() => {
        setCarlines([]);
        if (!isSignedIn || !modelYear) {
            return;
        }
        const controller = new AbortController();
        docsService
            .getCarlinesByModelYear({ modelYear, signal: controller.signal })
            .then((response) => {
                if (!controller.signal.aborted && response.success) {
                    setCarlines(
                        response.data.map((carline) => ({
                            id: carline.vehicleCarlineId,
                            description: carline.description,
                        }))
                    );
                }
            })
            .catch((error) =>
                logger.error(`Failed to fetch the carlines for model year [${modelYear}]`, { error, modelYear })
            );
        return () => controller.abort();
    }, [isSignedIn, modelYear]);

    useEffect(() => {
        setTrims([]);
        if (!isSignedIn || !modelYear || !carlineId) {
            return;
        }
        const controller = new AbortController();
        docsService
            .getVehiclesByModelYearAndCarline({ modelYear, carlineId, signal: controller.signal })
            .then((response) => {
                if (!controller.signal.aborted && response.success) {
                    setTrims(toTrimOptions(response.data));
                }
            })
            .catch((error) =>
                logger.error(`Failed to fetch the trims for model year [${modelYear}] and carline [${carlineId}]`, {
                    error,
                    modelYear,
                    carlineId,
                })
            );
        return () => controller.abort();
    }, [isSignedIn, modelYear, carlineId]);

    if (!isSignedIn) {
        return {
            modelYears: fallbackModelYears.map(String),
            carlines: fallbackCarlines,
            trims: [],
            supportsTrim: false,
        };
    }
    return { modelYears, carlines, trims, supportsTrim: true };
};
